import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import { useGetLpoQuery, useCreateLpoMutation, useUpdateLpoMutation } from "../../stockApi";
import { useGetMaintenanceRequestQuery } from "../../../masters/vehicles/vehiclesApi";
import VehicleRequestAttachment from "../../../masters/vehicles/components/VehicleRequestAttachment";
import { useHasPermission } from "../../../auth/useClaims";
import { useLazyGetCustomersQuery } from "../../../masters/customers/customersApi";
import { usePendingVehicleRequests } from "../usePendingVehicleRequests";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateLpo, useCanEditLpo, useCanEditApprovedLpo } from "../../useStockPermissions";
import PartyPicker from "../../components/PartyPicker";
import ApprovedQuotationPicker from "../../components/ApprovedQuotationPicker";
import LineItemsEditor, { emptyRow, computeTotals } from "../../components/LineItemsEditor";
import { useFillMissingUnits } from "../../components/lineUnits";
import { useQuotationGroupCheck } from "../useQuotationGroupCheck";
import { DEFAULT_VAT_PERCENT } from "../../../../components/common/vatDefaults";
import { useDispatch } from "react-redux";
import { quotationsApi, useLazyGetQuotationQuery } from "../../../sales/quotations/quotationsApi";
import { useLazyGetProductsQuery } from "../../../masters/products/productsApi";
import { paths } from "../../../../routes/paths";
import { useDefaultFormBranch } from "../../../branchFilter/useDefaultFormBranch";
import {
  allocationExtraColumns,
  derivedLpoPurpose,
  purchaseTypeOf,
  useAllocationCustomerOptions,
} from "../lpoLineAllocation";

// Alphabetical (2026-09-23, Batch196). OTHER is not offered any more - it is only kept
// selectable on an existing LPO that already carries it, so that LPO can still be edited.
const PURPOSE_TYPES = [
  { value: "CONSTRUCTION", label: "Construction" },
  { value: "CUSTOMER_SPECIFIC", label: "Customer Specific" },
  { value: "GENERAL_REPLENISHMENT", label: "General Replenishment" },
  { value: "GUARDING", label: "Guarding" },
  { value: "MAINTENANCE", label: "Maintenance" },
  { value: "MARKETING", label: "Marketing" },
  { value: "VEHICLE_MAINTENANCE", label: "Vehicle Maintenance" },
];
const LEGACY_PURPOSE_TYPES = [{ value: "OTHER", label: "Other (no longer offered)" }];

const PAYMENT_TYPES = [
  { value: "CASH", label: "Cash" },
  { value: "CREDIT", label: "Credit" },
];

const columns = { showRate: true, showVat: true, showRemarks: true, showReason: false, showMaxQty: false };

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

// LPO create/edit form. Two entry points:
// 1. Plain "New LPO" from the list - blank form.
// 2. "Convert to LPO" from PendingVehicleRequests.jsx - arrives via
//    location.state.fromVehicleRequest = {recordGuid, requestNumber}; this
//    form fetches that request's own detail (for its spare items and
//    branch) and prefills purposeType/vehicleRequestRecordGuid/lines. Still
//    fully editable before submit - this is a prefill, not an auto-submit.
const LpoForm = () => {
  const { recordGuid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = Boolean(recordGuid);
  const fromVehicleRequest = location.state?.fromVehicleRequest;

  const canCreate = useCanCreateLpo();
  const canEdit = useCanEditLpo();
  const canEditApproved = useCanEditApprovedLpo();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: lpo, isLoading: isLoadingLpo, isError: isLpoError, error: lpoError } = useGetLpoQuery(recordGuid, { skip: !isEditing });
  const [createLpo, { isLoading: isCreating }] = useCreateLpoMutation();
  const [updateLpo, { isLoading: isUpdating }] = useUpdateLpoMutation();
  const [fetchQuotation] = useLazyGetQuotationQuery();
  const [searchProducts] = useLazyGetProductsQuery();
  const [searchCustomers] = useLazyGetCustomersQuery();
  const fillMissingUnits = useFillMissingUnits();

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!isEditing && !branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [supplierOption, setSupplierOption] = useState(null);
  const [refNo, setRefNo] = useState("");
  const [lpoDate, setLpoDate] = useState(toDateInput(new Date()));
  const [purposeType, setPurposeType] = useState(() => (fromVehicleRequest ? "VEHICLE_MAINTENANCE" : "GENERAL_REPLENISHMENT"));
  const [purposeDescription, setPurposeDescription] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [customerOption, setCustomerOption] = useState(null);
  const [quotationRecordGuid, setQuotationRecordGuid] = useState("");
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  // {recordGuid, requestNumber} - seeded up front when arriving via the
  // "Convert to LPO" navigation state, otherwise set by the inline picker
  // below (purposeType === VEHICLE_MAINTENANCE, mandatory either way).
  const [vehicleRequest, setVehicleRequest] = useState(() =>
    fromVehicleRequest ? { recordGuid: fromVehicleRequest.recordGuid, requestNumber: fromVehicleRequest.requestNumber } : null
  );
  const [appliedRequestGuid, setAppliedRequestGuid] = useState(null);
  // 16% on a new LPO; an existing one loads its own saved rate below.
  const [vatRate, setVatRate] = useState(DEFAULT_VAT_PERCENT);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [lines, setLines] = useState([{ ...emptyRow(), vatPercent: String(DEFAULT_VAT_PERCENT) }]);
  // Batch185 - colours any line whose product group is not on the linked quotation.
  // Batch219 - only the lines bought against THAT quotation are checked; warehouse-stock
  // lines and other customers' lines on a mixed LPO were never meant to be on it.
  const groupCheckRows = useMemo(
    () => lines.filter((l) => purchaseTypeOf(l) === "CUSTOMER" && l.quotationRecordGuid === quotationRecordGuid),
    [lines, quotationRecordGuid]
  );
  const groupCheck = useQuotationGroupCheck({ quotationRecordGuid, branchId, rows: groupCheckRows });

  const { pendingRequests } = usePendingVehicleRequests(isEditing || purposeType !== "VEHICLE_MAINTENANCE", branchId ? Number(branchId) : null);

  // Same request detail fetch regardless of whether it arrived via the
  // "Convert to LPO" navigation state or was picked from the inline
  // dropdown below - one apply-effect handles both, and re-fires whenever
  // the selected request actually changes (not just once on mount), so
  // switching the picker's selection re-seeds branch/lines from the newly
  // picked request too.
  const { data: sourceRequest } = useGetMaintenanceRequestQuery(vehicleRequest?.recordGuid, {
    skip: !vehicleRequest?.recordGuid || isEditing,
  });
  // The linked request's attachment stays in view while converting / editing the LPO
  // (2026-09-23, "visible at any time even when converting to lpo"). Read on its own so
  // the prefill effect above keeps its create-only skip; RTK serves both from one fetch.
  const { data: linkedRequest } = useGetMaintenanceRequestQuery(vehicleRequest?.recordGuid, { skip: !vehicleRequest?.recordGuid });
  const canCreateVehicleRequest = useHasPermission("Vehicles.CreateRequest");
  const canApproveVehicleRequest = useHasPermission("Vehicles.ApproveRequest");
  const canManageRequestAttachment = canCreateVehicleRequest || canApproveVehicleRequest;

  useEffect(() => {
    if (isEditing || !sourceRequest || appliedRequestGuid === sourceRequest.recordGuid) return;
    setBranchId(sourceRequest.branchId);
    setPurposeDescription((prev) => prev || sourceRequest.serviceRepairDescription || "");
    if (sourceRequest.spareItems?.length) {
      // A spare item's unit is free text typed in the Flutter app and is
      // usually blank - these rows are locked, so the picker's own auto-fill
      // can never run here. Resolve it from the Product Master instead.
      const spareRows = sourceRequest.spareItems.map((item) => ({
        ...emptyRow(),
        productCode: item.productCode,
        productName: item.productName || "",
        unit: item.unit || "",
        qty: item.qty ?? "",
        rate: item.estimatedCost ?? "",
        vatPercent: String(DEFAULT_VAT_PERCENT),
        locked: true,
      }));
      setLines(spareRows);
      fillMissingUnits(spareRows, sourceRequest.branchId).then((filled) => {
        if (filled !== spareRows) setLines(filled);
      });
    }
    // The request's SpareSupplierName is plain free text typed at request
    // time, not linked to Customer Master - look it up here so the LPO's
    // supplier picker defaults to the matching customer when one exists by
    // exact name, instead of forcing the operator to re-search/re-select a
    // party that's already on file. Falls back to the typed text as a
    // creatable (unlinked) option when there's no exact match, same shape
    // PartyPicker itself produces for a typed-but-not-found party.
    const supplierName = sourceRequest.spareSupplierName?.trim();
    if (supplierName) {
      searchCustomers({ search: supplierName, isActive: true })
        .unwrap()
        .then((matches = []) => {
          const exact = matches.find((c) => c.custName?.trim().toLowerCase() === supplierName.toLowerCase());
          setSupplierOption(
            exact
              ? { value: exact.custId, label: `${exact.custName} (${exact.branchCode || exact.custId})`, customer: exact }
              : { value: null, label: supplierName, customer: null }
          );
        })
        .catch(() => setSupplierOption({ value: null, label: supplierName, customer: null }));
    }
    setAppliedRequestGuid(sourceRequest.recordGuid);
  }, [isEditing, sourceRequest, appliedRequestGuid, searchCustomers, fillMissingUnits]);

  // Populate from an existing LPO when editing.
  useEffect(() => {
    if (!isEditing || !lpo) return;
    setBranchId(lpo.branchId);
    setSupplierOption(
      lpo.supplierName
        ? {
            value: lpo.supplierId ?? null,
            label: lpo.supplierName,
            customer: lpo.supplierId != null ? { custId: lpo.supplierId, branchId: lpo.supplierBranchId, custName: lpo.supplierName } : null,
          }
        : null
    );
    setRefNo(lpo.refNo || "");
    setLpoDate(toDateInput(lpo.lpoDate));
    setPurposeType(lpo.purposeType);
    setPurposeDescription(lpo.purposeDescription || "");
    setPaymentType(lpo.paymentType || "");
    setCustomerOption(
      lpo.customerId ? { value: lpo.customerId, label: lpo.customerName || String(lpo.customerId), customer: { custId: lpo.customerId, branchId: lpo.customerBranchId, custName: lpo.customerName } } : null
    );
    setVehicleRequest(lpo.vehicleRequestRecordGuid ? { recordGuid: lpo.vehicleRequestRecordGuid, requestNumber: lpo.vehicleRequestNumber } : null);
    setQuotationRecordGuid(lpo.quotationRecordGuid || "");
    setVatRate(lpo.vatRate || 0);
    setDiscountAmount(lpo.discountAmount || 0);
    setRemarks(lpo.remarks || "");
    const savedRows = lpo.lines?.length
      ? lpo.lines.map((l) => ({
          ...emptyRow(),
          productCode: l.productCode,
          productName: l.productName,
          unit: l.unit || "",
          qty: l.orderedQty,
          rate: l.unitRate,
          vatPercent: l.vatPercent,
          remarks: l.remarks || "",
          // Batch219 - the line's own allocation.
          purchaseType: purchaseTypeOf(l),
          custOption:
            l.custId != null
              ? { value: `${l.custBranchId}-${l.custId}`, label: l.customerName || String(l.custId), customer: { custId: l.custId, branchId: l.custBranchId, custName: l.customerName } }
              : null,
          quotationRecordGuid: l.quotationRecordGuid || "",
        }))
      : [{ ...emptyRow(), vatPercent: String(lpo.vatRate ?? DEFAULT_VAT_PERCENT) }];
    setLines(savedRows);
    // Lines saved before the product got its unitLabel come back blank -
    // resolve those from the master so an edit doesn't re-save the blank.
    fillMissingUnits(savedRows, lpo.branchId).then((filled) => {
      if (filled !== savedRows) setLines(filled);
    });
  }, [isEditing, lpo, fillMissingUnits]);

  // A previously-picked quotation almost certainly belongs to the old
  // customer - carrying it over silently would be wrong, same reasoning as
  // QuotationCreateModal's own customer-change reset.
  useEffect(() => {
    setQuotationRecordGuid("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerOption?.customer?.custId, customerOption?.customer?.branchId]);

  // Auto-fill line items from an approved Quotation's PRODUCT-type lines
  // only (free-text quotation lines have no product to receive against, an
  // LPO line always needs one) - qty comes from the quotation, but
  // rate/unit/VAT are re-resolved fresh from the Product Master's current
  // purchase-side values (the quotation's own rate is customer-facing sales
  // pricing, a completely different number from what this branch pays a
  // supplier), same source ProductPicker itself already reads from.
  const handleQuotationChange = async (recordGuid) => {
    setQuotationRecordGuid(recordGuid || "");
    if (!recordGuid || !branchId) return;

    setIsAutoFilling(true);
    try {
      const quotation = await fetchQuotation(recordGuid).unwrap();
      const productLines = (quotation.lines || []).filter((l) => l.lineType === "PRODUCT" && l.productCode);
      // Batch185 - a free-text line tagged with a product group. It names no
      // product, so it is never expanded into one (a group can hold dozens):
      // it becomes ONE empty row whose product search is narrowed to that group,
      // with the quoted qty ready. The same group quoted on several lines still
      // yields one row - the buyer orders a model, not a quotation line.
      const groupLines = new Map();
      (quotation.lines || []).forEach((l) => {
        if (l.lineType !== "FREETEXT" || l.productGroupId == null) return;
        const entry = groupLines.get(l.productGroupId) || { groupId: l.productGroupId, qty: 0, unit: l.unit || "" };
        entry.qty += Number(l.qty) || 0;
        groupLines.set(l.productGroupId, entry);
      });

      if (productLines.length === 0 && groupLines.size === 0) {
        toast.error("This quotation has no product or product-group lines to fill from");
        return;
      }

      // Batch219 - the filled lines are Customer Job lines for the header customer and this
      // quotation. Lines already on the form for other purposes (warehouse stock, another
      // customer) stay; only blank rows and the lines a previously picked header quotation
      // filled are replaced.
      const allocation = {
        purchaseType: customerOption ? "CUSTOMER" : "STOCK",
        custOption: customerOption || null,
        quotationRecordGuid: customerOption ? recordGuid : "",
      };
      const newRows = [];
      for (const line of productLines) {
        const { data: matches = [] } = await searchProducts({ branchId: Number(branchId), search: String(line.productCode) });
        const product = matches.find((p) => String(p.productCode) === String(line.productCode));
        newRows.push({
          ...emptyRow(),
          productCode: line.productCode,
          productName: product?.productName || line.description,
          unit: product?.unitLabel || line.unit || "",
          qty: String(line.qty),
          rate: product?.purchaseRate != null ? String(product.purchaseRate) : "",
          // The product's own VAT when the master has it, else this LPO's default rate.
          vatPercent: product?.vat != null ? String(product.vat) : String(vatRate),
          productGroupId: product?.productGroupId ?? "",
          productGroupName: product?.productGroupName ?? "",
          ...allocation,
        });
        if (!product) {
          toast.error(`Product ${line.productCode} not found in this branch's Product Master - line added without a rate, at the default VAT`);
        }
      }
      groupLines.forEach((g) => {
        newRows.push({ ...emptyRow(), qty: String(g.qty), unit: g.unit, vatPercent: String(vatRate), pickFromGroupId: g.groupId, ...allocation });
      });
      setLines((prev) => [
        ...prev.filter((r) => r.productCode && !(quotationRecordGuid && r.quotationRecordGuid === quotationRecordGuid)),
        ...newRows,
      ]);
    } catch (e) {
      toast.error(e?.message || "Could not load the quotation");
    } finally {
      setIsAutoFilling(false);
    }
  };

  // Conditionally mandatory (2026-09-16). Linking the LPO to its quotation is
  // what makes the delivery-vs-quotation reconciliation able to see the
  // "purchased for this job" leg at all - 0 of 95 live LPOs carried one because
  // the field was purely optional and nobody used it.
  //
  // Batch219: the link is per line now. A Customer Job line whose customer has
  // approved quotations must name one (checked in handleSave, one request per
  // customer - the same list query the line's own picker already cached); a
  // customer with none stays saveable. Enforced on CREATE only, as before -
  // existing LPOs predate this and must stay correctable.
  const dispatch = useDispatch();
  const findMissingQuotation = async (validLines) => {
    const seen = new Set();
    for (const l of validLines) {
      const c = l.custOption?.customer;
      if (purchaseTypeOf(l) !== "CUSTOMER" || l.quotationRecordGuid || !c) continue;
      const key = `${c.branchId}-${c.custId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const page = await dispatch(
        quotationsApi.endpoints.listQuotations.initiate({ status: "APPROVED", custId: c.custId, branchId: c.branchId, pageSize: 100 }, { subscribe: false })
      )
        .unwrap()
        .catch(() => null);
      const count = (page?.items || []).length;
      if (count > 0) return { name: c.custName || l.custOption.label, count };
    }
    return null;
  };

  // Batch219 - the header purpose follows the lines: any Customer Job line makes the
  // LPO Customer Specific (the API applies the same rule; a vehicle maintenance LPO
  // keeps its purpose). All-stock lines keep the purpose picked here.
  const hasCustomerLines = lines.some((l) => l.productCode && purchaseTypeOf(l) === "CUSTOMER");
  const effectivePurpose = hasCustomerLines && purposeType !== "VEHICLE_MAINTENANCE" ? "CUSTOMER_SPECIFIC" : purposeType;
  const linesPurpose = derivedLpoPurpose(lines.filter((l) => l.productCode));
  const customerOptions = useAllocationCustomerOptions(
    effectivePurpose === "CUSTOMER_SPECIFIC" || lines.some((l) => purchaseTypeOf(l) === "CUSTOMER")
  );
  const extraColumns = useMemo(
    () => allocationExtraColumns({ customerOptions, defaultCustomer: customerOption, defaultQuotationRecordGuid: quotationRecordGuid }),
    [customerOptions, customerOption, quotationRecordGuid]
  );

  const totals = computeTotals(lines, columns);
  const netAmount = totals.subTotal - (Number(discountAmount) || 0) + totals.vatAmount;
  const isPostApprovalEdit = isEditing && lpo?.status === "APPROVED";
  const readOnly = isEditing ? (isPostApprovalEdit ? !(canEdit && canEditApproved) : !canEdit) : !canCreate;

  const handleSave = async () => {
    if (!branchId) return toast.error("Branch is required");
    const supplierName = (supplierOption?.customer?.custName || supplierOption?.label || "").trim();
    if (!supplierName) return toast.error("Supplier / party name is required");
    const validLines = lines.filter((l) => l.productCode && Number(l.qty) > 0);
    if (validLines.length === 0) return toast.error("Add at least one line with a product and quantity");
    if (!purposeType) return toast.error("Purpose is required");
    if (!purposeDescription.trim()) return toast.error("Purpose Description is required");
    if (!paymentType) return toast.error("Payment Type is required");
    const customerLines = validLines.filter((l) => purchaseTypeOf(l) === "CUSTOMER");
    if (purposeType === "CUSTOMER_SPECIFIC" && customerLines.length === 0)
      return toast.error("A Customer Specific LPO needs at least one Customer Job line - set the line's Purchase Type, or pick another purpose.");
    const noCustomer = customerLines.find((l) => !l.custOption?.customer);
    if (noCustomer) return toast.error(`Pick the customer for the Customer Job line "${noCustomer.productName}"`);
    if (!isEditing) {
      const missing = await findMissingQuotation(validLines);
      if (missing)
        return toast.error(
          `${missing.name} has ${missing.count} approved quotation${missing.count === 1 ? "" : "s"} - pick on each of its lines the one this LPO is buying for, so the cost can be traced back to what was quoted.`
        );
    }
    if (purposeType === "VEHICLE_MAINTENANCE" && !vehicleRequest?.recordGuid) return toast.error("Select the vehicle maintenance request");
    if (isEditing && !changeSummary.trim()) return toast.error(isPostApprovalEdit ? "A reason is required to edit an approved LPO" : "Describe what changed before resubmitting");

    const body = {
      branchId: Number(branchId),
      supplierId: supplierOption?.customer?.custId ?? null,
      supplierBranchId: supplierOption?.customer?.branchId ?? null,
      supplierName,
      refNo: refNo.trim() || null,
      lpoDate: new Date(lpoDate).toISOString(),
      purposeType: effectivePurpose,
      purposeDescription: purposeDescription.trim(),
      // The API re-derives the header customer/quotation from the lines (one customer = that
      // customer, several = none).
      customerId: effectivePurpose === "CUSTOMER_SPECIFIC" ? customerOption?.customer?.custId ?? null : null,
      customerBranchId: effectivePurpose === "CUSTOMER_SPECIFIC" ? customerOption?.customer?.branchId ?? null : null,
      vehicleRequestRecordGuid: vehicleRequest?.recordGuid ?? null,
      quotationRecordGuid: quotationRecordGuid || null,
      paymentType,
      subTotal: totals.subTotal,
      vatRate: Number(vatRate) || 0,
      vatAmount: totals.vatAmount,
      discountAmount: Number(discountAmount) || 0,
      netAmount,
      remarks: remarks.trim() || null,
      lines: validLines.map((l) => ({
        productCode: Number(l.productCode),
        productName: l.productName,
        orderedQty: Number(l.qty),
        unit: l.unit || null,
        unitRate: Number(l.rate) || 0,
        vatPercent: Number(l.vatPercent) || 0,
        vatAmount: (Number(l.qty) || 0) * (Number(l.rate) || 0) * ((Number(l.vatPercent) || 0) / 100),
        lineTotal: (Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 + (Number(l.vatPercent) || 0) / 100),
        remarks: l.remarks || null,
        linePurchaseType: purchaseTypeOf(l),
        custId: purchaseTypeOf(l) === "CUSTOMER" ? l.custOption?.customer?.custId ?? null : null,
        custBranchId: purchaseTypeOf(l) === "CUSTOMER" ? l.custOption?.customer?.branchId ?? null : null,
        quotationRecordGuid: purchaseTypeOf(l) === "CUSTOMER" ? l.quotationRecordGuid || null : null,
      })),
    };

    try {
      if (isEditing) {
        await updateLpo({ recordGuid, ...body, changeSummary: changeSummary.trim() }).unwrap();
        toast.success(isPostApprovalEdit ? "LPO updated - it remains approved" : "LPO updated and resubmitted");
        navigate(paths.stockLpoDetail(recordGuid), { replace: true });
      } else {
        const created = await createLpo(body).unwrap();
        toast.success("LPO submitted");
        navigate(paths.stockLpoDetail(created.recordGuid), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save the LPO");
    }
  };

  if (isEditing && isLpoError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to LPOs
          </Link>
          <div className="alert alert-danger">Could not load this LPO ({lpoError?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingLpo) {
    return (
      <div className="page-wrapper">
        <div className="content"><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>{isEditing ? `Edit LPO ${lpo?.lpoNumber || ""}` : "New LPO"}</h4>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isPostApprovalEdit
              ? "Editing an already-approved LPO requires the Stock.EditApprovedLpo permission (assigned individually to specific users)."
              : `${isEditing ? "Editing" : "Creating"} LPOs requires the Stock.${isEditing ? "EditLpo" : "CreateLpo"} permission.`}
          </div>
        )}

        {!readOnly && isPostApprovalEdit && (
          <div className="alert alert-warning">
            This LPO is already approved. Saving will update it directly and it will <strong>stay approved</strong> - no re-approval will be required. This change is fully logged.
          </div>
        )}

        {vehicleRequest && (
          <div className="alert alert-success">
            Linked to Vehicle Maintenance Request <strong>{vehicleRequest.requestNumber}</strong>
            {linkedRequest && (
              <div className="mt-2">
                <VehicleRequestAttachment
                  recordGuid={linkedRequest.recordGuid}
                  hasAttachment={linkedRequest.hasAttachment}
                  fileName={linkedRequest.attachmentFileName}
                  canManage={canManageRequestAttachment}
                />
              </div>
            )}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input type="text" className="form-control" value={lpo?.branchCode || ""} readOnly disabled />
                ) : (
                  <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={readOnly}>
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Supplier / Party (Customer Master)</label>
                <PartyPicker
                  mode="all-branches"
                  value={supplierOption}
                  onChange={setSupplierOption}
                  disabled={readOnly}
                  creatable
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">LPO Date</label>
                <input type="date" className="form-control" value={lpoDate} onChange={(e) => setLpoDate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Reference No</label>
                <input type="text" className="form-control" value={refNo} onChange={(e) => setRefNo(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Payment Type (required)</label>
                <select className="form-control" value={paymentType} onChange={(e) => setPaymentType(e.target.value)} disabled={readOnly}>
                  <option value="">--Select--</option>
                  {PAYMENT_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Purpose (required)</label>
                {/* Batch219 - a Customer Job line fixes the purpose to Customer Specific. */}
                <select
                  className="form-control"
                  value={effectivePurpose}
                  onChange={(e) => setPurposeType(e.target.value)}
                  disabled={readOnly || Boolean(vehicleRequest) || effectivePurpose !== purposeType}
                >
                  {[...PURPOSE_TYPES, ...LEGACY_PURPOSE_TYPES.filter((p) => p.value === purposeType)].map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {hasCustomerLines && <div className="small text-muted mt-1">Set by the lines: {linesPurpose.label}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Purpose Description (required)</label>
                <input type="text" className="form-control" value={purposeDescription} onChange={(e) => setPurposeDescription(e.target.value)} disabled={readOnly} />
              </div>
              {effectivePurpose === "CUSTOMER_SPECIFIC" && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Customer (optional) - fills new Customer Job lines</label>
                  <PartyPicker mode="all-branches" value={customerOption} onChange={setCustomerOption} disabled={readOnly} />
                </div>
              )}
              {effectivePurpose === "CUSTOMER_SPECIFIC" && customerOption && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Approved Quotation (optional) - adds its items as Customer Job lines
                  </label>
                  <ApprovedQuotationPicker
                    custId={customerOption.customer?.custId}
                    customerBranchId={customerOption.customer?.branchId}
                    value={quotationRecordGuid}
                    onChange={handleQuotationChange}
                    disabled={readOnly || isAutoFilling}
                  />
                </div>
              )}
              {purposeType === "VEHICLE_MAINTENANCE" && (
                <div className="col-md-6 mb-3">
                  <label className="form-label">Vehicle Maintenance Request (required)</label>
                  {isEditing ? (
                    <input type="text" className="form-control" value={vehicleRequest?.requestNumber || ""} readOnly disabled />
                  ) : (
                    <select
                      className="form-control"
                      value={vehicleRequest?.recordGuid || ""}
                      onChange={(e) => {
                        const guid = e.target.value;
                        const picked = pendingRequests.find((r) => r.recordGuid === guid);
                        setVehicleRequest(guid ? { recordGuid: guid, requestNumber: picked?.requestNumber } : null);
                      }}
                      disabled={readOnly}
                    >
                      <option value="">--Select--</option>
                      {vehicleRequest && !pendingRequests.some((r) => r.recordGuid === vehicleRequest.recordGuid) && (
                        <option value={vehicleRequest.recordGuid}>{vehicleRequest.requestNumber}</option>
                      )}
                      {pendingRequests.map((r) => (
                        <option key={r.recordGuid} value={r.recordGuid}>{r.requestNumber} - {r.regNo} ({r.brand} {r.model})</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            <hr />
            <h5 className="mb-3">Line Items</h5>
            {groupCheck.summary && (groupCheck.summary.outside > 0 || groupCheck.summary.over > 0) && (
              <div className="alert alert-warning py-2 small">
                Checked against the linked quotation:{" "}
                {groupCheck.summary.outside > 0 && (
                  <strong className="text-danger">
                    {groupCheck.summary.outside} line{groupCheck.summary.outside === 1 ? "" : "s"} not on the quotation
                  </strong>
                )}
                {groupCheck.summary.outside > 0 && groupCheck.summary.over > 0 && " · "}
                {groupCheck.summary.over > 0 && (
                  <strong>
                    {groupCheck.summary.over} line{groupCheck.summary.over === 1 ? "" : "s"} over the quoted quantity
                  </strong>
                )}
                . You can still save - the same colours show on the LPO for whoever approves it.
              </div>
            )}
            <LineItemsEditor
              branchId={branchId ? Number(branchId) : undefined}
              rows={lines}
              onChange={setLines}
              rowFlags={groupCheck.rowFlags}
              columns={columns}
              disabled={readOnly}
              defaultVatPercent={vatRate}
              extraColumns={extraColumns}
            />
            <div className="small text-muted mt-1">
              Each line is bought either for warehouse stock or for a customer job. The same product can be on several lines when the allocation differs.
            </div>

            <div className="row mt-3">
              <div className="col-md-3 mb-3">
                <label className="form-label">Default VAT %</label>
                <input type="number" className="form-control" value={vatRate} onChange={(e) => setVatRate(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Discount Amount</label>
                <input type="number" className="form-control" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} disabled={readOnly} />
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-end gap-4">
                  <div>Sub Total: <strong>{totals.subTotal.toFixed(2)}</strong></div>
                  <div>VAT: <strong>{totals.vatAmount.toFixed(2)}</strong></div>
                  <div>Net Amount: <strong>{netAmount.toFixed(2)}</strong></div>
                </div>
              </div>
              <div className="col-md-8 mb-3">
                <label className="form-label">Remarks</label>
                <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={readOnly} />
              </div>
              {isEditing && (
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    {isPostApprovalEdit ? "Reason for editing this approved LPO (required)" : "What changed? (required to resubmit)"}
                  </label>
                  <textarea className="form-control" rows={2} value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} disabled={readOnly} />
                </div>
              )}
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.stockLpos)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LpoForm;
